import Style from "ol/style/Style";
import Stroke from "ol/style/Stroke";
import Fill from "ol/style/Fill";
import CircleStyle from "ol/style/Circle";
import MultiPoint from "ol/geom/MultiPoint";
import VectorLayer from "ol/layer/Vector";
import Select from "ol/interaction/Select";
import Modify from "ol/interaction/Modify";
import Snap from "ol/interaction/Snap";
import Translate from "ol/interaction/Translate";
import Draw from "ol/interaction/Draw";
import { platformModifierKeyOnly } from "ol/events/condition";
import WKB from "ol/format/WKB";

const normalSelectStyle = new Style({
    stroke: new Stroke({ color: "#f8ff33", width: 2 }),
    fill: new Fill({ color: "rgba(102, 255, 51, 0.3)" })
});

const editingSelectStyle = (feature) => {
    const geom = feature.getGeometry();
    const type = geom.getType();
    const styles = [
        new Style({
            stroke: new Stroke({ color: 'blue', width: 3 }),
            fill: new Fill({ color: 'rgba(0, 0, 255, 0.1)' })
        })
    ];

    if (type === 'Polygon' || type === 'MultiPolygon') {
        const coordinates = type === 'Polygon'
            ? geom.getCoordinates()[0]
            : geom.getCoordinates().flatMap(poly => poly[0]);
        styles.push(new Style({
            image: new CircleStyle({ radius: 5, fill: new Fill({ color: 'orange' }) }),
            geometry: new MultiPoint(coordinates)
        }));
    } else if (type === 'LineString' || type === 'MultiLineString') {
        const coordinates = type === 'LineString'
            ? geom.getCoordinates()
            : geom.getCoordinates().flat();
        styles.push(new Style({
            image: new CircleStyle({ radius: 5, fill: new Fill({ color: 'orange' }) }),
            geometry: new MultiPoint(coordinates)
        }));
    } else if (type === 'Point') {
        styles.push(new Style({
            image: new CircleStyle({ radius: 8, fill: new Fill({ color: 'orange' }) })
        }));
    }
    return styles;
};

export default class GeometryEditorCore {
    constructor(map, vectorLayer, logger, opts = {}) {
        this.map = map;
        this.layer = vectorLayer;
        this.source = vectorLayer.getSource();
        this.logger = logger
        this.max = opts.max_stack_size || 50; // BUG FIX: was 10, too low for real use
        this.onDrawEnd = opts.onDrawEnd || null;
        this.onStatusChange = opts.onStatusChange || null;
        this.onCommit = opts.onCommit || null;

        this.mode = "none";
        this.editing = false;
        this.undoStack = [];
        this.redoStack = [];

        this.geomType = this._detectGeom();
        this._buildInteractions();
        this._setupModifyTranslateEvents();
        this._setupDrawEvents();
        this._updateInteractions();
    }

    // ─────────────────────────────────────────────────
    // GEOMETRY DETECTION
    // ─────────────────────────────────────────────────
    _detectGeom() {
        const geomType = this.layer.get('geometry_type');

        if (!geomType) {
            this.logger(`cannot initiate Editor for unknown goemtry type layer (${geomType})`, 'warning')
        }
        return geomType;
    }

    // ─────────────────────────────────────────────────
    // INTERACTION SETUP
    // ─────────────────────────────────────────────────
    _buildInteractions() {
        this.select = new Select({
            layers: [this.layer],
            multi: true,
            style: (feature) => this.editing ? editingSelectStyle(feature) : normalSelectStyle
        });
        this.map.addInteraction(this.select);

        // Modify — handles vertex editing on selected features.
        // Works on normal drag without any modifier key.
        this.modify = new Modify({
            source: this.source,
            features: this.select.getFeatures()
        });

        // Translate — moves whole features.
        // Requires Ctrl (or Cmd on Mac) to be held down so it doesn't
        // conflict with Modify. Without this condition both interactions
        // compete for the same mousedown event: Modify wins on vertices,
        // Translate wins on feature bodies — unpredictable behaviour.
        // platformModifierKeyOnly = Ctrl on Win/Linux, Cmd on Mac.
        this.translate = new Translate({
            layers: [this.layer],
            features: this.select.getFeatures(),
            condition: platformModifierKeyOnly,
        });

        // Snap — collect one Snap per VectorLayer so the cursor snaps to
        // features on ALL loaded vector layers, not just the active one.
        // This is important for alignment between layers.
        this.snaps = [];
        this.map.getLayers().forEach(layer => {
            if (layer instanceof VectorLayer) {
                this.snaps.push(new Snap({ source: layer.getSource() }));
            }
        });

        this.draw = new Draw({
            source: this.source,
            type: this.geomType
        });
    }

    _setupModifyTranslateEvents() {
        // ── MODIFY ──────────────────────────────────────────────────────────
        //
        // WHY NOT USE modifystart TO CAPTURE ORIGINAL GEOMETRY:
        // OL's Modify interaction fires modifystart with an inconsistent
        // feature collection — it may be empty or stale at that point because
        // OL hasn't fully resolved which feature the pointer is on yet.
        // This means f._originalGeom is often undefined by the time modifyend
        // fires, making undo impossible.
        //
        // THE FIX — capture geometry on the FIRST geometry change event:
        // We listen to the source's "changefeature" event. The very first time
        // a feature changes during an active modify operation we snapshot its
        // geometry BEFORE the change (by cloning before OL writes the new coords).
        // We use a Set (this._modifying) to track which features have already
        // been snapshotted so we only capture the pre-change state once per
        // drag operation.
        //
        // On modifyend we read the final geometry and push one undo entry
        // per modified feature, then clear the tracking set.

        this._modifying = new Set(); // tracks features currently being modified

        this._onSourceChangeFeature = (e) => {
            // Only track during an active modify operation
            if (!this._isModifying) return;

            const f = e.feature;
            const id = f.getId();

            // Only snapshot the FIRST change — this is the pre-edit geometry.
            // changefeature fires before OL re-renders, so the geometry on
            // the feature is still the OLD value at this point.
            if (!this._modifying.has(id)) {
                this._modifying.add(id);
                f._originalGeom = f.getGeometry().clone();
            }
        };

        this.source.on("changefeature", this._onSourceChangeFeature);

        this.modify.on("modifystart", () => {
            // Signal that a modify drag is in progress
            this._isModifying = true;
        });

        this.modify.on("modifyend", (e) => {
            this._isModifying = false;

            const updates = [];
            e.features.forEach(f => {
                const original = f._originalGeom;
                if (!original) return;

                const finalGeom = f.getGeometry();

                // Use coordinate comparison — extent comparison is insufficient
                // for cases like moving a single vertex of a polygon where the
                // bounding box may not change (e.g. moving an interior vertex).
                const changed =
                    JSON.stringify(original.getCoordinates()) !==
                    JSON.stringify(finalGeom.getCoordinates());

                if (changed) {
                    updates.push({
                        id: f.getId(),
                        prev_geom: original,
                        geom: finalGeom.clone()
                    });
                }

                delete f._originalGeom;
            });

            // Clear tracking set for next operation
            this._modifying.clear();

            if (updates.length > 0) {
                this._pushUndo({ type: "update", data: updates });
            }
        });

        // ── TRANSLATE ────────────────────────────────────────────────────────
        // Translate is simpler — translatestart fires reliably with all
        // selected features already in the collection.

        this.translate.on("translatestart", (e) => {
            e.features.forEach(f => {
                f._originalGeom = f.getGeometry().clone();
            });
        });

        this.translate.on("translateend", (e) => {
            const updates = e.features.getArray()
                .filter(f => {
                    if (!f._originalGeom) return false;
                    // Extent comparison is fine for translate — the whole
                    // feature moves so the extent always changes if it moved.
                    const moved =
                        f._originalGeom.getExtent().toString() !==
                        f.getGeometry().getExtent().toString();
                    return moved;
                })
                .map(f => {
                    const geom = f.getGeometry().clone();
                    const prev = f._originalGeom;
                    delete f._originalGeom;
                    return { id: f.getId(), prev_geom: prev, geom };
                });

            // Clean up any features that didn't actually move
            e.features.getArray().forEach(f => { delete f._originalGeom; });

            if (updates.length > 0) {
                this._pushUndo({ type: "update", data: updates });
            }
        });
    }

    _setupDrawEvents() {
        this.draw.on("drawstart", () => {
            this._onBackspace = (ev) => {
                if (ev.key === 'Backspace' || ev.keyCode === 8) {
                    ev.preventDefault();
                    this.draw.removeLastPoint();
                }
            };
            window.addEventListener("keydown", this._onBackspace);
        });

        this.draw.on("drawend", (e) => {
            if (this._onBackspace) {
                window.removeEventListener("keydown", this._onBackspace);
                this._onBackspace = null;
            }

            const f = e.feature;
            if (!f.getId()) f.setId(f.ol_uid);

            if (this.onDrawEnd) this.onDrawEnd(f);

            const clone = f.clone();
            clone.setId(f.getId());

            this._pushUndo({
                type: "add",
                data: [{ id: f.getId(), feature: clone, geom: clone.getGeometry().clone() }]
            });
        });
    }

    // ─────────────────────────────────────────────────
    // SELECTION HELPERS
    // ─────────────────────────────────────────────────
    _cleanSelection() {
        const selected = this.select.getFeatures();
        const toRemove = [];
        selected.forEach(f => {
            if (!this.source.getFeatureById(f.getId())) toRemove.push(f);
        });
        toRemove.forEach(f => selected.remove(f));
    }

    _refreshSelectionStyle() {
        this.select.getFeatures().forEach(f => f.changed());
    }

    // ─────────────────────────────────────────────────
    // STATUS
    // ─────────────────────────────────────────────────
    _emitStatus() {
        if (this.onStatusChange) this.onStatusChange(this.status());
    }

    status() {
        return {
            editing: this.editing,
            mode: this.mode,
            undo: this.undoStack.length,
            redo: this.redoStack.length,
            features: this.source.getFeatures().length
        };
    }

    // ─────────────────────────────────────────────────
    // MODE CONTROL
    // ─────────────────────────────────────────────────
    startEdit() {
        this.editing = true;
        this.enableSelectMode();
        this._refreshSelectionStyle();
        this._emitStatus();
    }

    stopEdit() {
        // BUG FIX: original stopEdit() wiped source.clear() unconditionally —
        // that means even committed/existing features were deleted from the map.
        // stopEdit should ONLY clear the undo/redo stacks and interactions,
        // NOT wipe features. Features belong to the layer, not the editor session.
        this.undoStack = [];
        this.redoStack = [];
        this._cleanSelection();
        this._refreshSelectionStyle();
        this.editing = false;
        this.mode = "none";
        this._updateInteractions();
        this._emitStatus();
    }

    enableSelectMode() {
        if (!this.editing) return;
        this.mode = "select";
        this._updateInteractions();
        this._emitStatus();
    }

    enableDrawMode() {
        if (!this.editing) return;
        this.mode = "draw";
        this.select.getFeatures().clear();
        this._updateInteractions();
        this._emitStatus();
    }

    // ─────────────────────────────────────────────────
    // INTERACTION MANAGEMENT
    // ─────────────────────────────────────────────────
    _updateInteractions() {
        // Remove all editable interactions first
        this.map.removeInteraction(this.modify);
        this.map.removeInteraction(this.translate);
        this.map.removeInteraction(this.draw);
        this.snaps.forEach(s => this.map.removeInteraction(s));

        if (!this.editing) return;

        if (this.mode === "select") {
            // Add Modify first — it takes priority over Translate.
            // Translate only fires when Ctrl is held (see condition in _buildInteractions),
            // so the two no longer conflict.
            this.map.addInteraction(this.modify);
            this.map.addInteraction(this.translate);
            this.snaps.forEach(s => this.map.addInteraction(s));
        } else if (this.mode === "draw") {
            this.map.addInteraction(this.draw);
            this.snaps.forEach(s => this.map.addInteraction(s));
        }
    }

    // ─────────────────────────────────────────────────
    // DELETE
    // ─────────────────────────────────────────────────
    deleteSelected() {
        if (!this.editing || this.mode !== "select") return;

        const features = this.select.getFeatures().getArray();
        if (features.length === 0) return;

        const deleteData = features.map(f => {
            const clone = f.clone();
            clone.setId(f.getId());
            this.source.removeFeature(f);
            return { id: f.getId(), feature: clone };
        });

        this.select.getFeatures().clear();
        this._pushUndo({ type: "delete", data: deleteData });
        this._emitStatus();
    }

    // ─────────────────────────────────────────────────
    // UNDO / REDO
    // ─────────────────────────────────────────────────
    _pushUndo(action) {
        this.undoStack.push(action);
        if (this.undoStack.length > this.max) this.undoStack.shift();
        this.redoStack = [];
        this._emitStatus();
    }

    undo() {
        if (!this.undoStack.length) return;
        const action = this.undoStack.pop();
        const { type, data } = action;

        if (type === "add") {
            data.forEach(item => {
                const f = this.source.getFeatureById(item.id);
                if (f) this.source.removeFeature(f);
            });
        } else if (type === "delete") {
            data.forEach(item => this.source.addFeature(item.feature));
        } else if (type === "update") {
            data.forEach(item => {
                const f = this.source.getFeatureById(item.id);
                if (f) f.setGeometry(item.prev_geom.clone());
            });
        }

        this.redoStack.push(action);
        this._cleanSelection();
        this._refreshSelectionStyle();
        this._emitStatus();
    }

    redo() {
        if (!this.redoStack.length) return;
        const action = this.redoStack.pop();
        const { type, data } = action;

        if (type === "add") {
            data.forEach(item => this.source.addFeature(item.feature));
        } else if (type === "delete") {
            data.forEach(item => {
                const f = this.source.getFeatureById(item.id);
                if (f) this.source.removeFeature(f);
            });
        } else if (type === "update") {
            data.forEach(item => {
                const f = this.source.getFeatureById(item.id);
                if (f) f.setGeometry(item.geom.clone());
            });
        }

        this.undoStack.push(action);
        this._cleanSelection();
        this._refreshSelectionStyle();
        this._emitStatus();
    }

    // ─────────────────────────────────────────────────
    // COMMIT
    // BUG FIX: original commit() called onCommit BEFORE clearing stacks,
    // then cleared stacks anyway — meaning if the user cancelled the dialog
    // in the UI, the stacks were still cleared and changes were lost.
    // Now commit() only builds and returns the summary. The UI calls
    // confirmCommit() when the user confirms, or rollback() to undo all.
    // ─────────────────────────────────────────────────
    commit() {
        const commits = new Map();
        const created = new Set();
        const wkt = new WKB();
 
        for (const { type, data } of this.undoStack) {
            for (const log of data) {
                if (type === "add") created.add(log.id);
                commits.set(log.id, {
                    type: created.has(log.id) ? "NEW" : "OLD",
                    state: type,
                    geom: log.geom
                });
            }
        }
 
        for (const [id, commit] of commits.entries()) {
            if (commit.type === "NEW" && commit.state === "delete") {
                commits.delete(id);
                continue;
            }
            if (commit.type === "NEW") commit.state = "add";
        }
 
        // ── Build API payload ──────────────────────────────────
        const layerId      = this.layer.get("layer_id");
        const geometryType = this.layer.get("geometry_type").toUpperCase();
 
        const payload = { create: [], update: [], delete: [] };
 
        for (const [id, commit] of commits.entries()) {
            const feature    = this.source.getFeatureById(id);
            const geomWkt    = feature?.getGeometry()
                ? wkt.writeGeometry(feature.getGeometry())
                : null;
            const properties = feature
                ? Object.fromEntries(
                    Object.entries(feature.getProperties())
                        .filter(([k]) => k !== "geometry")
                  )
                : {};
 
            if (commit.state === "add") {
                payload.create.push({
                    layer_id:      layerId,
                    geometry_type: geometryType,
                    geometry:      geomWkt,
                    properties,
                });
            } else if (commit.state === "update") {
                payload.update.push({
                    layer_id:      layerId,
                    id:            id,
                    geometry_type: geometryType,
                    geometry:      geomWkt,
                    properties,
                });
            } else if (commit.state === "delete") {
                payload.delete.push({
                    layer_id:      layerId,
                    id:            id,
                    geometry_type: geometryType,
                });
            }
        }
 
        // Pass both summary map and ready-to-send payload to UI
        if (this.onCommit) this.onCommit(commits, payload);
 
        // BUG FIX: do NOT clear stacks here anymore.
        // confirmCommit() clears them after user confirms in UI.
    }

    // Called by UI after user confirms commit
    confirmCommit() {
        this.undoStack = [];
        this.redoStack = [];
        this._emitStatus();
        this.source.refresh();
    }

    // Called by UI for rollback all — undoes every action in the stack
    rollbackAll() {
        this.undoStack = [];
        this.redoStack = [];
        this._emitStatus();
        this.source.refresh();
    }

    // ─────────────────────────────────────────────────
    // DESTROY — call this when the component unmounts
    // BUG FIX: original code set editorRef.current = null in useEffect cleanup
    // but never removed OL interactions from the map, leaving them dangling.
    // ─────────────────────────────────────────────────
    destroy() {
        // Clean up backspace listener if draw was active
        if (this._onBackspace) {
            window.removeEventListener("keydown", this._onBackspace);
            this._onBackspace = null;
        }

        // Remove source listener
        if (this._onSourceChangeFeature) {
            this.source.un("changefeature", this._onSourceChangeFeature);
            this._onSourceChangeFeature = null;
        }

        // Remove all interactions from the map
        this.map.removeInteraction(this.select);
        this.map.removeInteraction(this.modify);
        this.map.removeInteraction(this.translate);
        this.map.removeInteraction(this.draw);
        this.snaps.forEach(s => this.map.removeInteraction(s));
        this.snaps = [];

        this.undoStack = [];
        this.redoStack = [];
    }
}